import { useTheme } from '@mui/material';
import { CampaignStatus } from 'generated-graphql/graphql';
import { CampaignStatus as DeprecatedCampaignStatus } from 'services/campaign/types';

import EditIcon from '@mui/icons-material/Edit';
import CachedIcon from '@mui/icons-material/Cached';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

export const useStatusProps = (
  status: CampaignStatus | DeprecatedCampaignStatus,
  {
    messagesProcessedCount,
    totalProspectsCount
  }: {
    messagesProcessedCount?: number | null;
    totalProspectsCount?: number | null;
  } = {}
) => {
  const {
    palette: { success, customGrey, warning, error, primary }
  } = useTheme();

  if (status === DeprecatedCampaignStatus.DRAFT) {
    return {
      text: 'Draft',
      color: customGrey.dark,
      backgroundColor: customGrey.light,
      icon: (
        <EditIcon
          fontSize="small"
          style={{
            color: customGrey.dark
          }}
        />
      )
    };
  }

  if (status === DeprecatedCampaignStatus.SCHEDULED) {
    return {
      text: 'Scheduled',
      color: warning.contrastText,
      backgroundColor: warning.main,
      icon: (
        <CalendarTodayIcon
          fontSize="small"
          style={{
            color: warning.contrastText
          }}
        />
      )
    };
  }

  if (status === DeprecatedCampaignStatus.SENDING_MESSAGES) {
    return {
      text: 'Sending',
      color: customGrey.dark,
      backgroundColor: customGrey.light,
      icon: (
        <SendIcon
          fontSize="small"
          style={{
            color: customGrey.dark
          }}
        />
      )
    };
  }

  if (status === DeprecatedCampaignStatus.GENERATING_MESSAGES) {
    return {
      text: 'Generating messages',
      color: customGrey.dark,
      backgroundColor: customGrey.light,
      icon: (
        <CachedIcon
          fontSize="small"
          style={{
            color: customGrey.dark
          }}
        />
      )
    };
  }

  if (status === DeprecatedCampaignStatus.FINISHED_GENERATING) {
    const isSomeMessagesSent =
      messagesProcessedCount &&
      totalProspectsCount &&
      totalProspectsCount - messagesProcessedCount > 0;

    return {
      text: `Ready for launch ${
        isSomeMessagesSent ? `(Candidates awaiting)` : ''
      }`,
      color: primary.main,
      backgroundColor: primary.light,
      icon: (
        <CheckIcon
          style={{
            color: primary.main
          }}
        />
      )
    };
  }

  if (
    status === DeprecatedCampaignStatus.FINISHED_SENDING ||
    status === DeprecatedCampaignStatus.LIVE
  ) {
    return {
      text: 'Live',
      color: success.contrastText,
      backgroundColor: success.main,
      icon: (
        <FiberManualRecordIcon
          fontSize="small"
          style={{
            color: success.contrastText,
            fontSize: '0.6rem'
          }}
        />
      )
    };
  }

  if (status === DeprecatedCampaignStatus.DISCONNECTED) {
    return {
      text: 'Disconnected',
      color: customGrey.dark,
      backgroundColor: customGrey.light
    };
  }

  if (status === DeprecatedCampaignStatus.ARCHIVED) {
    return {
      text: 'Archived',
      color: error.main,
      backgroundColor: error.light,
      icon: <DeleteOutlinedIcon fontSize="small" color="error" />
    };
  }

  return {
    text: 'Loading',
    color: customGrey.dark,
    backgroundColor: customGrey.light
  };
};
